import { z } from 'zod';
import {
  IdSchema,
  TimestampsSchema,
  booleanQueryParam,
  paginated,
  softBoolean,
  softString,
} from './common.schema';

import { MediaSchema } from './media.schema';
import { BlockedByActivationRuleFilterSchema } from './activation-block.schema';

// Enum de razones por las que un pointer no es editable desde el área.
// Peer confirma que es enum estable, pensado para ramificar por código.
// Mantenemos `passthrough` implícito con `z.string()` fallback por si el
// back agrega valores nuevos — no rompemos parse.
export const EditabilityReasonSchema = z.union([
  // Config del area: pointer en `readOnlyFields`. Se lee, no se escribe.
  // El back marca `blockedBy: []` con este reason (release 2026-08-21).
  z.literal('areaReadOnly'),
  z.literal('blockedBySource'),
  z.literal('masterNotInPriority'),
  z.literal('unionWithoutMaster'),
  z.literal('mediaNotRendered'),
  // Razones específicas de media:
  z.literal('areaShowsNoMedia'),
  z.literal('sourceNotRendered'),
  z.literal('rowKindNotRendered'),
  // `urlNeverFromMaster` removido del enum del back en release b7054cb
  // (2026-08-28) -- ahora que `overrideSourceImage.visibleAspects` incluye
  // `url` en todos los modos, la razon nunca se dispara. El fallback
  // `z.string()` sigue absorbiendolo si un legacy payload llegara.
  z.literal('overrideOnlyFillsGaps'),
  z.literal('placementAlwaysFromOverride'),
  // Fallback tolerante a valores futuros del back.
  z.string(),
]);
export type EditabilityReason = z.infer<typeof EditabilityReasonSchema>;

// Aspectos visibles de una imagen o de una acción de media.
export const MediaAspectSchema = z.enum(['url', 'metadata', 'placement']);
export type MediaAspect = z.infer<typeof MediaAspectSchema>;

// Veredicto por acción sobre `/media`. `visible` decide si mostrar el botón
// de la acción; `visibleAspects` decide qué partes del control se habilitan.
export const MediaActionEditabilitySchema = z.object({
  visible: z.boolean(),
  visibleAspects: z.array(MediaAspectSchema).default([]),
  reason: EditabilityReasonSchema.optional(),
});
export type MediaActionEditability = z.infer<typeof MediaActionEditabilitySchema>;

// Veredicto por-pointer del back. Verificado por Backend PIM contra el
// código real (commit backend d48639e + docs/apis/products.md):
//
// - `reason` es el DISCRIMINANTE único. Siempre viaja con `editable: false`.
//   Enum estable — el front nunca debe ramificar por `blockedBy`.
// - `affectedAreas` falta cuando no hay nada que anunciar. La regla:
//   leerla SOLO si `editable === true`. Vacío = "solo cambia en tu área".
//   NUNCA es "propagación": el write siempre va a master; el array lista
//   las áreas cuya lente muestra el nuevo valor de master. Filtrar `master`
//   de la lista antes de mostrar (es tautológico — es donde ocurre).
// - `blockedBy` NO es discriminante: en campos de unión (applicationModels,
//   replacements, substitutes) las fuentes listadas NO bloquean nada — son
//   miembros del orden. Nunca pintarlo como chip con reason
//   `unionWithoutMaster` porque afirma algo falso.
export const PointerEditabilitySchema = z.object({
  editable: z.boolean(),
  affectedAreas: z.array(z.string()).optional(),
  blockedBy: z.array(z.string()).optional(),
  reason: EditabilityReasonSchema.optional(),
  // Solo aparece para `/media`: mapa de acciones con visible+visibleAspects.
  actions: z
    .object({
      uploadOwnImage: MediaActionEditabilitySchema.optional(),
      imageForWinningSource: MediaActionEditabilitySchema.optional(),
      overrideSourceImage: MediaActionEditabilitySchema.optional(),
    })
    .partial()
    .optional(),
});
export type PointerEditability = z.infer<typeof PointerEditabilitySchema>;

// Mapa completo devuelto por el back (`editability` del detalle) — pointer
// → veredicto. Notas importantes verificadas contra código del back:
//
// - El mapa lista SOLO pointers dentro del SCOPE del área (release
//   2026-08-21). Scope = `allowedFields ∪ keys(fieldPriorityConfig.fields)`
//   del área. Ausencia = no está en scope O no tiene write path (identidad
//   como /sku, /slug, /brandId, /categoryId, y JSONB intermedios como
//   /attributes o /attributes/pricing).
// - Ausencia NO es "bloqueado con reason" — el front NO debe pintar control
//   en ese caso.
// - `readOnlyFields` del área (config, no data) aparece como pointers con
//   `{ editable: false, blockedBy: [], reason: 'areaReadOnly' }` — se lee
//   con valor visible, se pinta readonly. Antes (pre-release) esa lista se
//   llamaba `hiddenFields` y ocultaba la key: ya no.
// - Las claves vienen ESCAPADAS RFC 6901 (`~0` para `~`, `~1` para `/`).
//   Nunca partir por `/` — usar lookup directo `map[pointer]`.
// - `brandId` y `categoryId` NUNCA aparecen en el mapa. El back traduce
//   internamente brandId→brand y categoryId→categories antes del guard.
//   Gatear inputs por `/brand` y `/categories`, nunca por los ids.
// - No usar el `jsonSchema` de /admin/products/schema como validador: el
//   back lo poda (93 de 161 pointers canónicos). Referencia:
//   `docs/apis/products.md` del back.
export const ProductEditabilitySchema = z.record(z.string(), PointerEditabilitySchema);
export type ProductEditability = z.infer<typeof ProductEditabilitySchema>;

// Sidecar de curaduría: valores que las fuentes enviaron pero que aún no
// están resueltos contra el catálogo maestro (categoría, marca, application
// models). Solo aparece cuando la request incluye `includePendings: true` y
// el caller es admin — el back lo suprime en el path B2B aunque lo pidan.
// `raw` es el texto accionable que hay que crear o aliasar.
export const PendingEntrySchema = z.object({
  raw: z.string(),
  normalized: z.string().nullable().optional(),
  sourceSlug: z.string(),
  detectedAt: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
});
export type PendingEntry = z.infer<typeof PendingEntrySchema>;

export const ProductPendingsSchema = z
  .object({
    category: PendingEntrySchema.nullable().optional(),
    brand: PendingEntrySchema.nullable().optional(),
    applicationModels: z.array(PendingEntrySchema).nullable().optional(),
  })
  .partial();
export type ProductPendingsSidecar = z.infer<typeof ProductPendingsSchema>;

// Sidecar de atribución de fuente por pointer. Solo llega si la request
// incluyó `includeFieldSources: true` (admin-only) — ver `getProduct`.
// Mapa JSON pointer -> array de slugs. Contrato medido contra prod
// (SKU 10091564, 46 entries):
//
// - SIEMPRE array, incluso con un solo slug. Varias fuentes contribuyen al
//   mismo pointer en campos de unión (`/attributes/replacements`,
//   `/attributes/substitutes`) — hay que pintar todos los slugs, no el primero.
// - Ausencia de una key = ninguna fuente informó ese pointer. Nunca `[]`:
//   vacío y ausente son cosas distintas y solo la segunda ocurre.
// - Granularidad = la HOJA que el back trata como tal. Un objeto anidado se
//   desciende: una medición `{value, unit, unitAssumed}` deja una entry POR
//   ESCALAR (`.../value`, `.../unit`) y el wrapper (`.../rim`) no aparece.
//   Un array es hoja propia: una entry en el pointer del array
//   (`/attributes/applicationModels`), nunca debajo. Un consumer que indexe
//   por el objeto envolvente no encuentra nada — hay que resolver por el
//   pointer EXACTO del control, sin fallback hacia arriba.
// - `/media` nunca aparece: resuelve por modo de consolidación de imagen,
//   no por prioridad de campo.
export const ProductFieldSourcesSchema = z.record(z.string(), z.array(z.string()));
export type ProductFieldSources = z.infer<typeof ProductFieldSourcesSchema>;

// Backend entity: Product. Read tolerante a campos faltantes.

export const ProductSchema = z
  .object({
    id: IdSchema,
    sku: softString,
    sourceClientId: IdSchema.nullable().optional(),
    // Backend devuelve `sourceClient` inline cuando se consulta con
    // ?sourceSlug=. Lo usamos para preservar la fuente al navegar al detalle.
    sourceClient: z
      .object({
        id: IdSchema,
        slug: z.string().nullable().optional(),
        name: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    npfCode: z.string().nullable().optional(),
    name: softString,
    // Promovido a top-level (antes vivía en attributes.commercialName).
    commercialName: z.string().nullable().optional(),
    slug: softString,
    categoryId: IdSchema.nullable().optional(),
    brandId: IdSchema.nullable().optional(),
    // Backend a veces devuelve `brand` como string (denormalizado) en list/detail
    // multi-source, otras como objeto. Tolerantes a ambos shapes.
    brand: z.union([z.string(), z.object({}).passthrough(), z.null()]).optional(),
    categories: z.array(z.string()).nullable().optional(),
    attributes: z.unknown().nullable().optional(),
    metadata: z.unknown().nullable().optional(),
    isActive: softBoolean,
    // `isWebActive` vive en `attributes.isWebActive` (shape canónico) pero
    // algunas projections del back lo flatean al root. Tolerantes: leemos
    // ambos con `readWebActive` en la UI. Ver packages/common `readWebActive`.
    isWebActive: z.boolean().nullable().optional(),
    media: z.array(MediaSchema).nullable().optional().default([]),
    // Solo llega si la request incluyó `includePendings: true` (admin-only).
    // Vacío `{}` significa "la fuente NO envió el dato" — no confundir con
    // "no lo pediste"; para eso el caller controla el flag desde el front.
    pendings: ProductPendingsSchema.nullable().optional(),
    // Solo llega si la request incluyó `area=<slug>`. Mapa por pointer:
    // qué es editable, qué áreas afecta el cambio, y por qué no si aplica.
    editability: ProductEditabilitySchema.nullable().optional(),
    // Solo llega si la request incluyó `includeFieldSources: true`
    // (admin-only). Ausente en backends viejos que no conocen el flag — la
    // pantalla debe funcionar igual sin este dato. Ver ProductFieldSourcesSchema.
    fieldSources: ProductFieldSourcesSchema.nullable().optional(),
  })
  .merge(TimestampsSchema.partial());

export type Product = z.infer<typeof ProductSchema>;

export const ProductListSchema = paginated(ProductSchema);
export type ProductList = z.infer<typeof ProductListSchema>;

// POST /products/query/counts (release hub-service-00167-cxd) --------------
// Reemplaza el fan-out de counters del dashboard "Pendientes" por UN request
// con un mapa `counts` (hasta 12 entries, peer cap). El back devuelve
// `{ counts, computedAt }` — counts es Record<label, number>. Los labels
// vienen definidos por el caller (`anyMissing`, `missingBrand`, ...). Sin
// passthrough: keys extra top-level se stripean (convención del proyecto).
// TTL server-side ~60s: el `staleTime` del hook consumer bounds worst-case
// staleness (60 + client staleTime) — no bumpear sin evaluar impacto.
export const ProductCountsResponseSchema = z.object({
  counts: z.record(z.string(), z.number().int().nonnegative()),
  computedAt: z.string().datetime(),
});
export type ProductCountsResponse = z.infer<typeof ProductCountsResponseSchema>;

// GET /products/attribute-values (release back 2026-10-05) -----------------
//
// Valores que toma un atributo en el producto consolidado, de mayor a menor
// cantidad de SKUs. `value` llega crudo, como lo guarda la fuente que gana (sin
// rótulo). `totalDistinct` cuenta los valores ANTES del recorte por `limit`;
// con `truncated: true` la lista está cortada y la pantalla tiene que decirlo.
export const AttributeValueSchema = z.union([z.string(), z.number(), z.boolean()]);
export type AttributeValue = z.infer<typeof AttributeValueSchema>;

export const AttributeValuesSchema = z.object({
  items: z.array(
    z.object({
      value: AttributeValueSchema,
      count: z.number().int().nonnegative(),
    }),
  ),
  totalDistinct: z.number().int().nonnegative(),
  truncated: z.boolean(),
});
export type AttributeValues = z.infer<typeof AttributeValuesSchema>;

// POST /products -----------------------------------------------------------

// SKU: backend hace strip silencioso de espacios (incluye internos), exige
// charset [A-Za-z0-9._-], min 2 después del strip, max 100, y al menos un
// alfanumérico (rechaza ".-_", "---"). Replicamos en cliente para feedback
// inmediato; el strip se aplica en el form input (paso ProductForm).
export const SkuSchema = z
  .string()
  .transform((s) => s.replace(/\s+/g, ''))
  .pipe(
    z
      .string()
      .min(2, 'Mínimo 2 caracteres tras quitar espacios')
      .max(100, 'Máximo 100 caracteres')
      .regex(/^[A-Za-z0-9._-]+$/, 'Solo A-Z, a-z, 0-9, ., - y _')
      .refine((s) => /[A-Za-z0-9]/.test(s), 'No puede ser solo separadores'),
  );

// brandId/categoryId: el form los maneja como string (HTML select), pero el
// backend los espera como integer >= 1. Aceptamos ambos tipos en el schema
// y convertimos a number en el boundary (handleSubmit del form).
const NumericIdInput = z.union([z.string(), z.number()]).optional();

export const ProductCreateSchema = z.object({
  sku: SkuSchema,
  name: z.string().min(1).max(240),
  commercialName: z.string().max(240).optional(),
  slug: z
    .string()
    .min(1)
    .max(240)
    .regex(/^[a-z0-9-]+$/, 'Solo minúsculas, números y guiones'),
  npfCode: z.string().max(60).optional(),
  categoryId: NumericIdInput,
  brandId: NumericIdInput,
  attributes: z.record(z.string(), z.unknown()).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  // Opcional: sin él master queda en `null` y deciden las reglas. Mandarlo es
  // un cambio manual (back 5467e65); el formulario lo manda solo si se tocó.
  isActive: z.boolean().optional(),
});
export type ProductCreate = z.infer<typeof ProductCreateSchema>;

// PATCH /products/:sku -----------------------------------------------------

// En edit mode, defaultValues traen `slug: ''` cuando el back devolvio
// `slug: null` (detail lo mapea con `?? ''`). RHF llama al resolver con
// el value completo del form -- no solo los dirty --, asi que un slug ""
// pre-existente disparaba `.min(1)` del CreateSchema y bloqueaba el submit
// silencioso (sin toast, sin error visible: `handleSubmit` nunca corria).
// El patch al back sigue armandose con `dirtyFields`, asi que "" nunca
// viaja. Toleramos "" solo en el update schema para no aflojar el create.
export const ProductUpdateSchema = ProductCreateSchema.partial()
  .omit({ sku: true })
  .extend({
    slug: z
      .string()
      .max(240)
      .regex(/^[a-z0-9-]*$/, 'Solo minúsculas, números y guiones')
      .optional(),
    npfCode: z.string().max(60).optional(),
  });
export type ProductUpdate = z.infer<typeof ProductUpdateSchema>;

// Filtros para listado -----------------------------------------------------

export const ProductListParamsSchema = z.object({
  search: z.string().optional(),
  categoryId: z.string().optional(),
  brandId: z.string().optional(),
  // Multi-source: array de slugs, CSV en URL. `undefined`/`[]` = sin param
  // (default backend = todas las fuentes activas). `['all']` = wildcard.
  sourceSlugs: z.array(z.string()).optional(),
  isActive: booleanQueryParam.optional(),
  // Filter por `/attributes/isWebActive` (release 2026-08-25-4). Boolean:
  // `true` = publicados en la web, `false` = no publicados. Resuelto sobre
  // el producto CONSOLIDADO (source con mayor prioridad para el pointer
  // gana). Un source que no reporta el flag NO compite — no es false.
  // Los productos que ningún source reportó caen en NINGUNA de las dos
  // ramas; su count se computa como `total(sin filter) - total(true) - total(false)`.
  isWebActive: booleanQueryParam.optional(),
  // Filtros de incompletitud (server-side). Antes se filtraba cliente-side
  // sobre la página actual — rompía paginación. Backend ya los expone.
  missingBrand: booleanQueryParam.optional(),
  missingCategory: booleanQueryParam.optional(),
  missingMedia: booleanQueryParam.optional(),
  // Release 2026-08-26: sellable-value filters. Semantics distintas al
  // clásico `missingPointers`:
  //   - `missingPrice: true` → sin PRECIO VENDIBLE en el consolidado. Un
  //     source ganador que reportó 0 cuenta como missing.
  //   - `missingStock: true` → sin EXISTENCIAS en el consolidado. Un source
  //     ganador que reportó 0 cuenta como missing.
  // Answer "can it be sold", not "did anyone write a number". No usar como
  // alias de `missingPointers['/attributes/pricing/basePrice']` — ése
  // pregunta si el pointer existe, no si es vendible.
  missingPrice: booleanQueryParam.optional(),
  missingStock: booleanQueryParam.optional(),
  // Lista de JSON pointers (ej: ['/brand', '/media']). Back devuelve SKUs
  // donde AL MENOS UNO de los pointers listados está vacío. Combina AND con
  // los demás filters. Reemplaza al endpoint /completeness/summary (eliminado).
  missingPointers: z.array(z.string()).optional(),
  // Filtro por valor de atributo (release back 2026-10-05). El producto entra
  // si su valor consolidado para el pointer es alguno de `values` (OR dentro
  // del campo, AND entre campos). Máximo 5 campos y 50 valores por campo; el
  // tipo cuenta (`2000` no es `"2000"`). Un pointer fuera de lo que lee el
  // área activa es 422.
  attributes: z
    .array(
      z.object({
        pointer: z.string(),
        values: z.array(AttributeValueSchema).min(1),
      }),
    )
    .optional(),
  // Fallback visual del listado: si `true`, el back rellena `media` con un
  // sello genérico (o TVS si la brand contiene "tvs") cuando el producto no
  // tiene imagen real. Solo se activa desde la ProductsListPage. No afecta
  // la lógica de "sin imagen" del completeness — el front detecta el mock
  // por URL prefix `/static/mock/`.
  mockPicture: booleanQueryParam.optional(),
  // Apagados por regla de activación: `isActive`, `isWebActive` o `any`
  // (cualquiera de los dos). Sale de los apagados por regla, no del valor del
  // producto. Solo en el admin: el API B2B lo rechaza con 400.
  blockedByActivationRule: BlockedByActivationRuleFilterSchema.optional(),
  // Rango de fechas (createdAt) — backend `withDateRange`. ISO 8601 con offset.
  from: z.string().optional(),
  to: z.string().optional(),
  skip: z.coerce.number().int().min(0).default(0),
  take: z.coerce.number().int().min(1).max(200).default(50),
});
export type ProductListParams = z.infer<typeof ProductListParamsSchema>;

// GET /admin/products/sources — catálogo liviano de fuentes activas para
// poblar el filtro multi-source. Devuelve sólo {slug, name}.
export const ProductSourceEntrySchema = z.object({
  slug: z.string(),
  name: z.string(),
});
export type ProductSourceEntry = z.infer<typeof ProductSourceEntrySchema>;
export const ProductSourcesResponseSchema = z.array(ProductSourceEntrySchema);

// Filtros opcionales del endpoint /admin/products/sources.
// canRead/canWrite filtran por capacidades del cliente; isActive por estado.
export interface ProductSourcesParams {
  canRead?: boolean;
  canWrite?: boolean;
  isActive?: boolean;
}

// GET /admin/products/schema (descubrimiento dinámico) ---------------------
// El backend devuelve la lista de campos editables con su tipo. La UI lo
// usa para construir el editor de atributos dinámicos del producto.

export const ProductFieldDefSchema = z.object({
  name: z.string(),
  type: z.enum(['string', 'number', 'boolean', 'enum', 'date', 'array', 'object']),
  required: z.boolean().default(false),
  enumValues: z.array(z.string()).optional(),
  description: z.string().optional(),
});
export type ProductFieldDef = z.infer<typeof ProductFieldDefSchema>;

// Backend new shape: además de fields/pointers (legacy), expone:
// - jsonSchema: schema raíz Draft 2020-12 con types reales por campo.
// - readablePointers / writablePointers: catálogos completos.
// - sourceCatalog: fuentes reales del sistema (axioma, master, sap…).
//
// Mantenemos `fields/pointers` opcionales para no romper mocks viejos.

export const SourceCatalogEntrySchema = z.object({
  id: z.coerce.number(),
  slug: z.string(),
  name: z.string(),
});
export type SourceCatalogEntry = z.infer<typeof SourceCatalogEntrySchema>;

export const ProductSchemaResponseSchema = z.object({
  schemaVersion: z.string().optional(),
  jsonSchema: z.record(z.string(), z.unknown()).optional(),
  readablePointers: z.array(z.string()).optional().default([]),
  writablePointers: z.array(z.string()).optional().default([]),
  sourceCatalog: z.array(SourceCatalogEntrySchema).optional().default([]),
  // Compat con el shape viejo (MSW / backends en transición).
  fields: z.array(ProductFieldDefSchema).optional().default([]),
  pointers: z.array(z.string()).optional().default([]),
});
export type ProductSchemaResponse = z.infer<typeof ProductSchemaResponseSchema>;

// Extended properties contracts (GET /admin/products/schema/extended-contracts)
// ---------------------------------------------------------------------------
// Backend declara 45 keys conocidas dentro de
// `attributes.specifications.extendedProperties` con contratos tipados:
// enum, boolean, integer con min/max, measurement {value, unit}, string maxLength.
// El objeto sigue siendo `additionalProperties: true` — keys desconocidas pasan
// libres. El front usa estos contratos para renderizar inputs tipados.

// Cada variante refleja uno de los 5 shapes que el back emite.
export const ExtendedContractSchema = z.union([
  z.object({
    type: z.literal('string'),
    enum: z.array(z.string()).optional(),
    maxLength: z.number().int().positive().optional(),
  }),
  z.object({ type: z.literal('boolean') }),
  z.object({
    type: z.literal('integer'),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
  }),
  z.object({
    type: z.literal('object'),
    required: z.array(z.string()),
    properties: z.object({
      value: z.object({ type: z.literal('number') }),
      unit: z.object({
        type: z.literal('string'),
        enum: z.array(z.string()),
      }),
    }),
  }),
]);
export type ExtendedContract = z.infer<typeof ExtendedContractSchema>;

export const ExtendedContractsResponseSchema = z.object({
  schemaVersion: z.string(),
  contracts: z.record(z.string(), ExtendedContractSchema),
});
export type ExtendedContractsResponse = z.infer<typeof ExtendedContractsResponseSchema>;

// Multipart media (POST /products/:sku/media) ------------------------------

export const ProductMediaUploadSchema = z.object({
  file: z
    .instanceof(File)
    .refine((f) => f.size <= 10 * 1024 * 1024, 'Máximo 10 MB')
    .refine(
      (f) => ['image/png', 'image/jpeg', 'image/webp'].includes(f.type),
      'Formato no permitido (png/jpeg/webp)',
    ),
  altText: z.string().max(240).optional(),
  title: z.string().max(240).optional(),
  isPrimary: z.boolean().optional(),
});
export type ProductMediaUpload = z.infer<typeof ProductMediaUploadSchema>;
